import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { avatarVariants } from './avatar.variants.ts'

/**
 * Wraps the avatar image and fallback and sets their shared size.
 *
 * @summary Avatar frame.
 */
function AvatarRoot(props: AvatarRoot.Props) {
  const { className, size = 'medium', ...rest } = props
  return <AvatarPrimitive.Root data-slot="avatar" className={cn(avatarVariants({ size, className }))} {...rest} />
}
namespace AvatarRoot {
  type Variants = NonNullable<VariantProps<typeof avatarVariants>>
  export type Props = AvatarPrimitive.Root.Props & {
    /** Diameter of the avatar. @default 'medium' */
    size?: NonNullable<Variants['size']> | undefined
  }
}

function AvatarImage(props: AvatarImage.Props) {
  const { className, ...rest } = props
  return (
    <AvatarPrimitive.Image data-slot="avatar-image" className={cn('size-full object-cover', className)} {...rest} />
  )
}
namespace AvatarImage {
  export type Props = AvatarPrimitive.Image.Props
}

function AvatarFallback(props: AvatarFallback.Props) {
  const { className, ...rest } = props
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn('flex size-full items-center justify-center font-500', className)}
      {...rest}
    />
  )
}
namespace AvatarFallback {
  export type Props = AvatarPrimitive.Fallback.Props
}

/**
 * An avatar composed of an image and a fallback, with a shared size on the root.
 *
 * @summary Image and fallback avatar.
 */
const Avatar = {
  Root: AvatarRoot,
  Image: AvatarImage,
  Fallback: AvatarFallback,
}

namespace Avatar {
  export namespace Root {
    export type Props = AvatarRoot.Props
  }
  export namespace Image {
    export type Props = AvatarImage.Props
  }
  export namespace Fallback {
    export type Props = AvatarFallback.Props
  }
}

export { Avatar, avatarVariants }
